import { useMemo, useState } from 'react';
import { useWorkspace } from '../app/WorkspaceProvider';
import { displayNameOf, useAuth } from '../auth/AuthProvider';
import { useGo } from '../components/Nav';
import { Banner, PageHeader, Select } from '../components/ui';
import { defaultRecordingTitle } from '../lib/format';
import type { Workspace } from '../lib/types';
import { orgAcceptsRecordings, subscriptionNotice } from '../queries/orgs';
import { createUploadTransport } from '../queries/videos';
import { FileUpload } from '../recorder/FileUpload';
import { Recorder } from '../recorder/Recorder';
import { RecorderPreview } from '../recorder/RecorderPreview';

/**
 * /new — the recorder, plus the one decision worth making before you hit
 * record: where the recording lands.
 */

interface Destination {
  workspace: Workspace;
  orgId: string | null;
  label: string;
}

export function NewPage() {
  const { user } = useAuth();
  const { orgs, activeOrg } = useWorkspace();
  const go = useGo();

  const destinations = useMemo<Destination[]>(() => {
    const list: Destination[] = [
      { workspace: 'personal', orgId: null, label: 'Personal library — only you' },
    ];
    for (const org of orgs) {
      list.push({
        workspace: 'team',
        orgId: org.id,
        label: `${org.name} — the team workspace`,
      });
    }
    return list;
  }, [orgs]);

  const [destinationKey, setDestinationKey] = useState(() =>
    activeOrg ? `team:${activeOrg.id}` : 'personal:',
  );

  const destination =
    destinations.find((item) => `${item.workspace}:${item.orgId ?? ''}` === destinationKey) ??
    destinations[0];

  const destinationOrg = destination.orgId
    ? (orgs.find((org) => org.id === destination.orgId) ?? null)
    : null;

  const notice = destinationOrg
    ? subscriptionNotice(destinationOrg.subscriptionStatus, destinationOrg.trialEndsAt)
    : null;

  const blocked = destinationOrg ? !orgAcceptsRecordings(destinationOrg.subscriptionStatus) : false;

  const transport = useMemo(() => createUploadTransport(), []);
  const makeTitle = () => defaultRecordingTitle(displayNameOf(user));

  return (
    <>
      <PageHeader
        title="New recording"
        subtitle="Pick a screen, hit record. Everything uploads while you talk."
      />

      {notice ? (
        <div className="mb-5">
          <Banner tone={notice.tone} title={notice.title}>
            {notice.body}
          </Banner>
        </div>
      ) : null}

      {blocked ? (
        <div className="mb-5">
          <Banner tone="danger" title="New recordings to this workspace are paused">
            Choose your personal library instead — that one is free and always available — or ask
            an owner to sort the subscription out.
          </Banner>
        </div>
      ) : null}

      {/* Deliberately not keyed on the destination: changing where a recording
          lands must never tear down a screen share the user already granted. */}
      <Recorder
        transport={transport}
        makeTitle={makeTitle}
        workspace={destination.workspace}
        orgId={destination.orgId}
        onCancel={() => go('/')}
        renderUnsupported={() => (
          <div className="space-y-3">
            <p className="text-body text-chalk-dim">
              You can still upload a video you already have — a screen recording from your phone,
              or an export from another tool.
            </p>
            <FileUpload
              transport={transport}
              makeTitle={(file) => file.name.replace(/\.[^.]+$/, '') || makeTitle()}
              workspace={destination.workspace}
              orgId={destination.orgId}
              onUploaded={(result) => go('/v/$shareId', { params: { shareId: result.shareId } })}
            />
          </div>
        )}
        renderPreview={(recorder) => (
          <RecorderPreview
            recorder={recorder}
            defaultTitle={makeTitle()}
            onDone={() => go('/')}
            onRecordAnother={() => recorder.reset()}
          />
        )}
      >
        {destinations.length > 1 ? (
          <div className="max-w-sm">
            <Select
              label="Save to"
              value={destinationKey}
              onChange={(event) => setDestinationKey(event.target.value)}
            >
              {destinations.map((item) => (
                <option key={`${item.workspace}:${item.orgId ?? ''}`} value={`${item.workspace}:${item.orgId ?? ''}`}>
                  {item.label}
                </option>
              ))}
            </Select>
          </div>
        ) : null}
      </Recorder>
    </>
  );
}
